import React from "react";

/**
 * Avatar — shared component for user/seller profile photos.
 *
 * Behavior:
 *  - `src` is preferred when present and non-empty.
 *  - Otherwise renders the first 1-2 initials from `name` over a tinted
 *    background derived from a hash of the name (stable across renders).
 *  - Always renders as a square circle. Use the `size` prop in px.
 *
 * The fallback is intentionally distinct (no plain gray) so the user sees
 * a real identity surface even before they upload a photo.
 */

const TONES = [
  // Pairs are [bg, fg]. Hand-picked to stay readable and on-brand.
  ["#0d6e4a", "#ffffff"], // mv green
  ["#14213d", "#ffffff"], // mv secondary
  ["#9bd424", "#0a2a17"], // mv accent
  ["#0ea5e9", "#ffffff"], // sky
  ["#7c3aed", "#ffffff"], // violet
  ["#db2777", "#ffffff"], // pink
  ["#f97316", "#ffffff"], // orange
  ["#0891b2", "#ffffff"], // cyan
];

function _hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function _initials(name) {
  if (!name) return "·";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Avatar({
  src,
  name = "",
  size = 40,
  className = "",
  testId,
  rounded = "rounded-full",
}) {
  const dims = { width: size, height: size };
  const has = !!src && typeof src === "string" && src.trim();
  if (has) {
    return (
      <img
        src={src}
        alt={name || ""}
        style={dims}
        className={`${rounded} object-cover bg-gray-100 ${className}`}
        data-testid={testId}
      />
    );
  }
  const [bg, fg] = TONES[_hash(name || "?") % TONES.length];
  const initials = _initials(name);
  return (
    <span
      aria-label={name || "user"}
      role="img"
      data-testid={testId || "avatar-fallback"}
      style={{ ...dims, backgroundColor: bg, color: fg, fontSize: size * 0.42 }}
      className={`${rounded} inline-flex items-center justify-center font-bold select-none ${className}`}
    >
      {initials}
    </span>
  );
}
